import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { playWheelSpinSound, playWinFanfare, playTickSound } from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const SpinWheel = ({ prizes = [], onFinished }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [pointerWobble, setPointerWobble] = useState(false);
  const cancelAudioRef = useRef(null);

  // Vibrant arcade color palette for the wheel slices
  const sliceColors = [
    '#F97316', // Orange 500
    '#0D9488', // Teal 600
    '#EAB308', // Yellow 500
    '#EC4899', // Pink 500
    '#2563EB', // Blue 600
    '#8B5CF6', // Violet 500
    '#EF4444', // Red 500
    '#10B981', // Emerald 500
  ];

  const safePrizes = prizes && prizes.length > 0 ? prizes : ["Prize 1", "Prize 2", "Try Again"];

  const spin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setPointerWobble(true);
    
    const settings = getSettings();
    const soundOn = settings.soundEnabled !== false;

    if (soundOn) {
      playTickSound(600, 0.08);
      cancelAudioRef.current = playWheelSpinSound(5000);
    }

    // Pick a winner randomly
    const prizeIndex = Math.floor(Math.random() * safePrizes.length);
    const sliceAngle = 360 / safePrizes.length;
    
    // Add 6 extra full rotations (2160 degrees) for visual excitement
    const extraSpins = 360 * 6;
    const currentMod = rotation % 360;
    // Target slice at the top (0 degrees)
    const targetAngle = 360 - (prizeIndex * sliceAngle);
    
    const nextRotation = rotation + extraSpins + targetAngle - currentMod;
    setRotation(nextRotation);

    // Stop sound and finish
    setTimeout(() => {
      setIsSpinning(false);
      setPointerWobble(false);
      if (cancelAudioRef.current) cancelAudioRef.current();

      if (soundOn) {
        playWinFanfare();
      }

      if (onFinished) onFinished(safePrizes[prizeIndex]);
    }, 5100);
  };

  const sliceAngle = 360 / safePrizes.length;
  
  // Generate CSS Conic Gradient for the background of the wheel
  const gradientStops = safePrizes.map((_, i) => {
    const start = i * sliceAngle;
    const end = (i + 1) * sliceAngle;
    return `${sliceColors[i % sliceColors.length]} ${start}deg ${end}deg`;
  }).join(', ');

  const totalBulbs = 16;

  return (
    <div className="relative w-full max-w-sm sm:max-w-md mx-auto aspect-square flex items-center justify-center p-3 select-none">
      
      {/* Outer Golden Arcade Frame with Flashing LED Casino Bulbs */}
      <div className="relative w-full h-full rounded-full border-[10px] sm:border-[12px] border-amber-500 shadow-[0_15px_45px_rgba(0,0,0,0.6),0_0_30px_rgba(245,158,11,0.3)] bg-gradient-to-b from-amber-600 via-amber-800 to-amber-950 p-1 flex items-center justify-center">
        
        {/* Flashing Perimeter Casino Bulbs */}
        {[...Array(totalBulbs)].map((_, i) => {
          const bulbAngle = (i * (360 / totalBulbs)) * (Math.PI / 180);
          // Position bulbs around the edge
          const radiusPercent = 48.5; // percentage from center
          const leftPos = 50 + radiusPercent * Math.sin(bulbAngle);
          const topPos = 50 - radiusPercent * Math.cos(bulbAngle);

          return (
            <div 
              key={i} 
              className={`absolute w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full z-20 pointer-events-none transition-all duration-300 shadow-sm border border-yellow-200 ${
                isSpinning 
                  ? (i % 2 === 0 ? 'bg-amber-200 shadow-[0_0_10px_#fde047]' : 'bg-red-400 shadow-[0_0_10px_#f87171]')
                  : 'bg-yellow-200 shadow-[0_0_6px_#fef08a]'
              }`}
              style={{
                left: `${leftPos}%`,
                top: `${topPos}%`,
                transform: 'translate(-50%, -50%)',
                animation: isSpinning ? `pulse 0.35s infinite alternate ${i * 0.05}s` : 'none'
              }}
            />
          );
        })}

        {/* 3D Tactile Flapper Pointer at the Top */}
        <div className="absolute -top-5 sm:-top-6 left-1/2 -translate-x-1/2 z-40 drop-shadow-[0_6px_8px_rgba(0,0,0,0.6)]">
          <motion.div 
            animate={{ rotate: pointerWobble ? [-8, 8, -6, 6, 0] : 0 }}
            transition={{ repeat: pointerWobble ? Infinity : 0, duration: 0.12 }}
            className="flex flex-col items-center"
          >
            <svg width="42" height="48" viewBox="0 0 24 28" fill="none">
              <path d="M12 28L2 4C2 2 4 0 12 0C20 0 22 2 22 4L12 28Z" fill="url(#pointerGold)" stroke="#78350f" strokeWidth="1.5" />
              <defs>
                <linearGradient id="pointerGold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#fef08a" />
                  <stop offset="50%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#b45309" />
                </linearGradient>
              </defs>
            </svg>
            <div className="w-3.5 h-3.5 rounded-full bg-white shadow-md border border-amber-600 -mt-9"></div>
          </motion.div>
        </div>

        {/* The Spinning Wheel Core */}
        <div className="w-full h-full rounded-full relative overflow-hidden shadow-inner border-4 border-amber-900/60">
          <motion.div 
            className="w-full h-full rounded-full relative overflow-hidden"
            style={{ 
              background: `conic-gradient(from -${sliceAngle/2}deg, ${gradientStops})`
            }}
            animate={{ rotate: rotation }}
            transition={{ duration: 5, ease: [0.15, 0.85, 0.2, 1] }}
          >
            {/* Wheel Text/Labels & Decorative Pegs */}
            {safePrizes.map((prize, i) => (
              <div 
                key={i}
                className="absolute top-1/2 left-1/2 origin-left"
                style={{
                  transform: `translateY(-50%) rotate(${i * sliceAngle - 90}deg)`,
                  width: '50%',
                  height: '42px'
                }}
              >
                {/* Metallic slice divider peg at outer rim */}
                <div className="absolute right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-gradient-to-r from-yellow-100 to-amber-400 border border-amber-600 shadow-sm z-10"></div>

                {/* Slice Prize Text */}
                <div className="pl-8 pr-4 w-full h-full flex items-center justify-end text-white font-extrabold text-xs sm:text-sm drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] tracking-tight text-right uppercase">
                  {prize}
                </div>
              </div>
            ))}
            
            {/* Inner decorative metallic ring */}
            <div className="absolute inset-0 rounded-full border-4 border-white/20 m-3 pointer-events-none"></div>
          </motion.div>
        </div>

        {/* Center Spin Arcade Gold Pushbutton Hub */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
          <button 
            type="button"
            onClick={spin}
            disabled={isSpinning}
            className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-b from-yellow-300 via-amber-400 to-amber-600 rounded-full flex flex-col items-center justify-center shadow-[0_8px_0_#78350f,0_12px_20px_rgba(0,0,0,0.6)] active:shadow-[0_2px_0_#78350f,0_4px_10px_rgba(0,0,0,0.6)] active:translate-y-1.5 border-4 border-yellow-100 text-red-950 font-black text-base sm:text-lg hover:scale-105 transition-all disabled:opacity-90 disabled:scale-100 disabled:cursor-not-allowed uppercase tracking-wider"
          >
            <span className="drop-shadow-sm leading-tight">
              {isSpinning ? "ROLL" : "SPIN"}
            </span>
            <span className="text-[8px] font-bold tracking-widest text-amber-900/80 -mt-0.5">
              {isSpinning ? "..." : "TAP"}
            </span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default SpinWheel;